import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { SITE, waLink } from "../data/site";
import { LogoMark } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useT } from "../i18n/LanguageContext";

export const Navbar = () => {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { label: t("nav.storia"), href: "#about", key: "storia" },
    { label: t("nav.menu"), href: "#menu", key: "menu" },
    { label: t("nav.recensioni"), href: "#recensioni", key: "recensioni" },
    { label: t("nav.galleria"), href: "#galleria", key: "galleria" },
    { label: t("nav.doveSiamo"), href: "#mappa", key: "doveSiamo" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[var(--cream)]/85 backdrop-blur-xl border-b border-[var(--sand)]/60 shadow-[0_4px_24px_rgba(42,31,24,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-10 h-[78px] flex items-center justify-between gap-4">
        <a href="#hero" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
          <LogoMark size={52} />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-xl text-[var(--espresso)]">Pizzeria da Sofia</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--gold-deep)]">Novara · Italia</div>
          </div>
        </a>

        <ul className="hidden lg:flex items-center gap-8 xl:gap-9">
          {links.map((l) => (
            <li key={l.key}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.key}`}
                className="text-[13px] uppercase tracking-[0.18em] text-[var(--espresso-soft)] hover:text-[var(--gold-deep)] transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher variant="desktop" />
          <a
            href={`tel:${SITE.phoneIntl}`}
            className="flex items-center gap-2 text-[13px] text-[var(--espresso-soft)] hover:text-[var(--gold-deep)] transition-colors"
            data-testid="navbar-phone"
          >
            <Phone className="w-4 h-4" /> {SITE.phone}
          </a>
          <a
            href={waLink(t("waMessages.book"))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta-whatsapp"
            className="btn-primary text-[12px] !py-3 !px-6"
          >
            {t("nav.prenota")}
          </a>
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <LanguageSwitcher variant="mobile-bar" />
          <button
            className="p-2 text-[var(--espresso)]"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-[var(--ivory)] border-t border-[var(--sand)] ${
          open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <li key={l.key}>
              <a
                href={l.href}
                onClick={() => setOpen(false)}
                className="block text-base uppercase tracking-[0.18em] text-[var(--espresso)]"
                data-testid={`mobile-nav-${l.key}`}
              >
                {l.label}
              </a>
            </li>
          ))}
          <li className="pt-4 border-t border-[var(--sand)] flex flex-col gap-3">
            <a href={`tel:${SITE.phoneIntl}`} className="text-sm text-[var(--espresso-soft)]">
              <Phone className="w-4 h-4 inline mr-2" /> {SITE.phone}
            </a>
            <a
              href={waLink(t("waMessages.book"))}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary justify-center"
              data-testid="mobile-cta-whatsapp"
            >
              {t("nav.prenotaWa")}
            </a>
          </li>
        </ul>
      </div>
    </header>
  );
};
